import { useState } from 'react';
import useSWR from 'swr';
import { useDebounce } from 'use-debounce';
// api
import getShippers from 'modules/emergencies/api/getShippers';

// returns selectedFilters and functions to update them
const useShippersFilter = ({ initialQuery = '' }) => {
  const initialSelectedFilters = {
    search: initialQuery,
  };

  const [internalSelectedFilters, setInternalSelectedFilters] = useState(initialSelectedFilters);
  const [selectedFilters] = useDebounce(internalSelectedFilters, 500);

  const filtersKey = JSON.stringify(selectedFilters);
  const { data: shippers, error: shippersError } = useSWR(
    `/shippers/${filtersKey}`,
    () => getShippers(internalSelectedFilters),
    {
      errorRetryCount: 0,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const isLoadingShippers = typeof shippers === 'undefined' && !shippersError;

  let shipperOptions = [];

  if (shippers) {
    shipperOptions = (shippers?.results || []).map((shipper) => ({
      value: shipper.id,
      shipperData: { ...shipper },
      label: <>{shipper.company_name}</>,
      display: shipper.company_name,
      search: `${shipper.company_name}`,
    }));
  }

  const setSearch = (value) => {
    setInternalSelectedFilters({
      ...internalSelectedFilters,
      search: value,
    });
  };

  const getShipperDataFromShipperPreferences = (shipperData) => {
    let phone = '';
    let name = '';
    let email = '';
    let preferred_contact_method = 'email';
    let lang = 'en';

    try {
      if (!!shipperData.users && shipperData.users.length > 0) {
        const user = shipperData.users[0];
        phone = user.phone;
        name = user.name;
        email = user.email;
        if (!!user.communication_preferences) {
          if (user.communication_preferences.language) {
            try {
              const shortLang = user.communication_preferences.language.split('-')[0];
              lang = shortLang;
            } catch (e) {
              console.error(e);
            }
          }
          const preferredChannel = user.communication_preferences?.channels.find(
            (channel) => channel.preferred
          );
          if (!!preferredChannel && preferredChannel.type === 'whatsapp') {
            preferred_contact_method = 'whatsapp';
          }
          const emailChannel = user.communication_preferences?.channels.find(
            (channel) => channel.type === 'email'
          );
          const phoneChannel = user.communication_preferences?.channels.find(
            (channel) => channel.type === 'whatsapp'
          );
          if (!!emailChannel && emailChannel.content && emailChannel.content.trim() !== '') {
            email = emailChannel.content;
          }
          if (!!phoneChannel && phoneChannel.content && phoneChannel.content.trim() !== '') {
            phone = phoneChannel.content;
          }
        }
      }
    } catch (e) {
      console.error(e);
    }

    const data = { phone, name, email, preferred_contact_method, lang };
    return data;
  };

  const setShipper = (value, form, selectedOption) => {
    if (typeof selectedOption === 'undefined') {
      form.setFieldValue(`shipper_id`, '');
      return;
    }
    const { shipperData } = selectedOption;
    const workingAreas = shipperData.working_areas || [];
    setShipperWorkingAreas(workingAreas);
    form.setFieldValue(`shipper_id`, shipperData.id);
    form.setFieldValue(`shipper_data.company_name`, shipperData.company_name);
    if (!!shipperData.external_id)
      form.setFieldValue(`shipper_data.company_id`, shipperData.external_id);
    const { phone, name, email, preferred_contact_method, lang } =
      getShipperDataFromShipperPreferences(shipperData);
    form.setFieldValue(`shipper_data.contact_name`, name);
    form.setFieldValue(`custom.transport.email`, email);
    form.setFieldValue(`shipper_data.phone`, phone);
    form.setFieldValue(`custom.transport.phone`, phone);
    form.setFieldValue(
      `custom.transport.selected_notification_transport`,
      preferred_contact_method
    );
    form.setFieldValue(`shipper_data.language`, lang);
  };

  const [shipperWorkingAreas, setShipperWorkingAreas] = useState([]);

  return {
    internalSelectedFilters,
    shipperOptions,
    shipperOptionsError: shippersError,
    isLoadingShipperOptions: isLoadingShippers,
    setSearch,
    setShipper,
    shipperWorkingAreas,
  };
};

export default useShippersFilter;
